import * as React from "react";

import { BindingPath, ExternalBindingPath } from "../Engine/Core/CoreTypes";
import { FullFormState } from "../Engine/Core/Store/FullFormState";
import { IForm } from "../Engine/Form/IForm";
import { ViewModelSelect } from "../Helpers/StateAccessors/ViewModelSelect";
import { AbsoluteModelPath, createFromMask, createModelPath, PathTokens } from "../../../Common/ModelPath/ModelPath";
import { getValueByPath, PrimitiveValue } from "../Helpers/StateAccessors/GetValueByPath";

import { PathResolver } from "./withResolvePaths";
import { createHocDisplayName } from "./TypingUtils";
import { useFormObject } from "./FormObjectProvider";
import { useVariablesContext } from "./Services/Contexts/VariablesContext";

export type ModelAccessor = (path: BindingPath, valueName?: string) => PrimitiveValue | string[];
export type ModelCountAccessor = (path: BindingPath) => number;

export interface ModelDependentEvaluator<T> {
    type: "ModelDependentEvaluator";
    expression: (path: ModelAccessor, count: ModelCountAccessor) => T;
}

export type EvaluableProp<T> = T | ModelDependentEvaluator<T>;

export function isModelDependentEvaluator<T>(value: undefined | EvaluableProp<T>): value is ModelDependentEvaluator<T> {
    return (
        value != undefined &&
        typeof value !== "number" &&
        typeof value !== "boolean" &&
        typeof value !== "string" &&
        typeof value !== "symbol" &&
        typeof value !== "bigint" &&
        typeof value === "object" &&
        "type" in value &&
        value.type === "ModelDependentEvaluator"
    );
}

function getCountByPath(
    _form: IForm,
    state: FullFormState,
    viewModelSelect: ViewModelSelect,
    parentPath: AbsoluteModelPath,
    path: ExternalBindingPath,
    _name: string = "value"
): number {
    if (path.startsWith("/")) {
        return viewModelSelect.children(state, `${path.substring(1)}`).length;
    } else {
        return viewModelSelect.children(state, parentPath.joinWith(createModelPath(path, false))).length;
    }
}

export interface EvaluatorsInjectableProps {
    processEvaluableProp: <T>(state: FullFormState, prop: EvaluableProp<T>) => T;
}

export interface EvaluatorsRequiredProps {
    evaluatorsContextPath?: AbsoluteModelPath;
    viewModelSelect: ViewModelSelect;
    resolvePath: PathResolver;
}

export function withEvaluators<P extends EvaluatorsInjectableProps>(
    WrappedComponent: React.ComponentType<P>
): React.ComponentType<Omit<P, keyof EvaluatorsInjectableProps> & EvaluatorsRequiredProps> {
    function WithEvaluatorsWrapper(
        props: Omit<P, keyof EvaluatorsInjectableProps> & EvaluatorsRequiredProps
    ): JSX.Element {
        const form = useFormObject();
        const viewModelSelect = props.viewModelSelect;
        const variableContext = useVariablesContext();
        const unresolvedEvaluatorsContextPath: AbsoluteModelPath | undefined = props.evaluatorsContextPath;
        const evaluatorsContextPath =
            unresolvedEvaluatorsContextPath != undefined
                ? props.resolvePath(unresolvedEvaluatorsContextPath)
                : undefined;

        const processEvaluableProp = <T,>(state: FullFormState, prop: EvaluableProp<T>): T => {
            if (isModelDependentEvaluator(prop)) {
                if (evaluatorsContextPath == undefined) {
                    throw new Error("EvaluatorsContextPath for component with evaluable props must be specified");
                }
                const pathFn: ModelAccessor = (path: ExternalBindingPath, valueName?: string) => {
                    if (path.startsWith("@")) {
                        return getValueByPath(
                            state,
                            viewModelSelect.customFieldValue.bind(viewModelSelect),
                            variableContext,
                            path,
                            valueName
                        ).toString();
                    } else {
                        const localPath = props.resolvePath(
                            evaluatorsContextPath.joinWith(createFromMask(path, "auto", PathTokens.each))
                        );
                        return getValueByPath(
                            state,
                            viewModelSelect.customFieldValue.bind(viewModelSelect),
                            variableContext,
                            localPath,
                            valueName
                        );
                    }
                };
                const countFn: ModelCountAccessor = (path: ExternalBindingPath) =>
                    getCountByPath(form, state, viewModelSelect, evaluatorsContextPath, path);

                return prop.expression(pathFn, countFn);
            }
            return prop;
        };

        // @ts-ignore Ну нехочет typescript работать с HOC-ами
        return <WrappedComponent {...props} processEvaluableProp={processEvaluableProp} />;
    }

    WithEvaluatorsWrapper.displayName = createHocDisplayName(WrappedComponent, "Evaluators");
    return WithEvaluatorsWrapper;
}
